import { Dispatch, SetStateAction } from "react";
import imglyRemoveBackground from "@imgly/background-removal";
import { GetSet, Pos2d } from "../types";
import { Box } from "konva/lib/shapes/Transformer";

export default class Utils {
  static async sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  static degreesToRadians(degrees: number) {
    return (degrees * Math.PI) / 180;
  }

  static generateRandomId(len = 10) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let id = "";
    while (len > 0) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
      len--;
    }

    return id;
  }

  static getCorner(
    pivotX: number,
    pivotY: number,
    diffX: number,
    diffY: number,
    angle: number
  ) {
    const distance = Math.sqrt(diffX * diffX + diffY * diffY);

    /// find angle from pivot to corner
    angle += Math.atan2(diffY, diffX);

    /// get new x and y and round it off to integer
    const x = pivotX + distance * Math.cos(angle);
    const y = pivotY + distance * Math.sin(angle);

    return { x, y };
  }
  static getCorners(box: Box): [Pos2d, Pos2d, Pos2d, Pos2d] {
    const { x, y, width, height } = box;
    const rad = box.rotation;

    const p1 = Utils.getCorner(x, y, 0, 0, rad);
    const p2 = Utils.getCorner(x, y, width, 0, rad);
    const p3 = Utils.getCorner(x, y, width, height, rad);
    const p4 = Utils.getCorner(x, y, 0, height, rad);

    return [p1, p2, p3, p4];
  }
  static getClientRectTopMidPos(corners: [Pos2d, Pos2d, Pos2d, Pos2d]) {
    const sortedCorners = corners.sort((a, b) => a.y - b.y);
    const [p1, p2] = sortedCorners;
    return { left: (p1.x + p2.x) / 2, top: (p1.y + p2.y) / 2 };
  }

  static getClientRect(box: Box) {
    const [p1, p2, p3, p4] = Utils.getCorners(box);

    const minX = Math.min(p1.x, p2.x, p3.x, p4.x);
    const minY = Math.min(p1.y, p2.y, p3.y, p4.y);
    const maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
    const maxY = Math.max(p1.y, p2.y, p3.y, p4.y);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      rotation: 0,
    } as Box;
  }
  static getIntersectionArea(canvaBox: Box, itemBox: Box) {
    let x1 = itemBox.x;
    let y1 = itemBox.y;
    let x2 = itemBox.x + itemBox.width;
    let y2 = itemBox.y + itemBox.height;

    let ix1 = Math.max(0, x1);
    let iy1 = Math.max(0, y1);
    let ix2 = Math.min(canvaBox.width, x2);
    let iy2 = Math.min(canvaBox.height, y2);

    let iw = Math.max(0, ix2 - ix1);
    let ih = Math.max(0, iy2 - iy1);

    return iw * ih;
  }
  static isInsideCanva(
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    canvaWidth: number,
    canvaHeight: number
  ) {
    const box = Utils.getClientRect({ x, y, width, height, rotation });
    const canvaBox = {
      x: 0,
      y: 0,
      width: canvaWidth,
      height: canvaHeight,
      rotation: 0,
    } as Box;
    /*
    return (
      box.x > 0 &&
      box.y > 0 &&
      box.x + box.width < canvaWidth &&
      box.y + box.height < canvaHeight
    );
    */

    const normalizedArea =
      Utils.getIntersectionArea(canvaBox, box) / (box.width * box.height);
    return normalizedArea > 0.1;
  }

  static capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  static getTrimmed<T>(obj: T): T {
    if (typeof obj === "string") {
      return obj.trim() as T;
    } else if (obj instanceof Array) {
      return obj.map(Utils.getTrimmed) as T;
    } else if (typeof obj === "object" && obj !== null) {
      const trimmedObj: any = {};
      for (const key in obj) {
        trimmedObj[key] = Utils.getTrimmed(obj[key]);
      }
      return trimmedObj;
    }

    return obj;
  }

  static async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(blob);
    });
  }
  static mirrorImage(image: HTMLImageElement): HTMLCanvasElement {
    // Cria um elemento canvas
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Contexto 2D não disponível.");
    }

    // Define as dimensões do canvas para corresponder à imagem
    canvas.width = image.width;
    canvas.height = image.height;

    // Espelha a imagem horizontalmente
    ctx.scale(-1, 1); // Inverte a escala no eixo X
    ctx.drawImage(image, -image.width, 0); // Desenha a imagem invertida no canvas

    return canvas;
  }
  /**
   * TIRA O FUNDO, E AGORA DIZ EM QUE PASSO ESTÁ — `T4.8`.
   *
   * ── O QUE FOI MEDIDO EM 28/09/2026, E DESMENTE A CAUSA REGISTRADA ─────────────────────────
   *
   * O `oQueFalta` do ponto dizia, desde 13/09, que **o recorte nunca rodou**, e dava a causa: o WASM
   * vem do unpkg em tempo de execução e o navegador barra por CORS. Medido de novo, na vitrine:
   *
   *     os CINCO arquivos da biblioteca ...... HTTP 200, do unpkg, sem uma linha de CORS no console
   *     (e o cabeçalho do unpkg, pedido à mão: Access-Control-Allow-Origin *, em GET e em OPTIONS)
   *
   * Então a causa registrada **não reproduz**. O que apareceu no lugar dela foram duas outras, e as
   * duas eram invisíveis enquanto se acreditava na primeira:
   *
   *   1. com a imagem da fixture — **2x2 pixels** — a biblioteca responde *"The source image could not
   *      be decoded"*. Não era o recorte que não rodava: era a imagem que não existia. A fixture ganhou
   *      um desenho de verdade por causa disto (ver `desenhoDeMentira`, na vitrine).
   *   2. com uma imagem de 256x256, o recorte roda **e não terminou em 180 s** — e a tela não dizia
   *      NADA nesse tempo. É a mesma forma do *"tá salvando infinitamente"* do `T6.4`: uma espera sem
   *      tamanho, e a criança sem saber se clicou.
   *
   * ── O QUE ESTA FUNÇÃO PASSA A FAZER, E O QUE ELA DELIBERADAMENTE NÃO DECIDE ────────────────
   *
   * Ela repassa o `progress` da biblioteca, que chega como `(chave, atual, total)`, para quem chamou —
   * e é isso que permite a tela dizer em que passo está, em vez de ficar muda. Dado real, e não
   * estimativa: cada passo é um arquivo que chegou ou não chegou.
   *
   * O QUE ELA NÃO MUDA, e é decisão do operador: o `model`. O padrão da biblioteca é `medium`, e são
   * **84,1 MB** de modelo mais 46 MB de WASM; o `small` são **42,3 MB**. Numa escola com rede
   * compartilhada isso é a diferença entre esperar e desistir — e o preço é qualidade de recorte, que é
   * o olho dele. Trocar é uma palavra aqui; escolher não é meu.
   */
  static async removeBG(
    imgLink: string,
    aoAndar?: (passo: string, atual: number, total: number) => void
  ) {
    const blob = await imglyRemoveBackground(imgLink, {
      progress: (chave: string, atual: number, total: number) => {
        aoAndar?.(chave, atual, total);
        return undefined;
      },
    });
    return URL.createObjectURL(blob);
  }

  static docsArrToDict<T>(arr: T[]): Record<string, T | undefined> {
    const dict: Record<string, T> = {};
    for (const item of arr) {
      dict[(item as any)["id"]] = item;
    }
    return dict;
  }
  static arrToDict<T>(arr: T[]): Record<number, T> {
    return arr.reduce((acc, value, index) => {
      acc[index] = value;
      return acc;
    }, {} as Record<number, T>);
  }
  static dictToArr<T>(dict: Record<number, T>): T[] {
    return Object.values(dict);
  }

  static stateToGetSet<T>(
    state: T,
    setState: Dispatch<SetStateAction<T>>
  ): GetSet<T> {
    return function (value?: T) {
      if (value !== undefined) {
        setState(value);
        return value;
      } else {
        return state;
      }
    };
  }

  static roundNumber(num: number, decimalPlaces = 2): number {
    const powerOf10 = Number(`1e${decimalPlaces}`);
    return Math.round((num + Number.EPSILON) * powerOf10) / powerOf10;
  }
}
